import { useEffect, useState } from "react";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { fetchShippedFeed } from "@/lib/api";

/**
 * ShippedFeedCard — calm "what we shipped from your feedback" feed.
 * Pure DB (feature_requests where status='shipped'). Renders only when at
 * least one shipped item exists, so the card stays out of the way on a
 * cold-start dashboard.
 */
export default function ShippedFeedCard({ limit = 4 }) {
    const [items, setItems] = useState(null);

    useEffect(() => {
        let mounted = true;
        fetchShippedFeed(limit)
            .then((rows) => {
                if (mounted) setItems(rows || []);
            })
            .catch(() => {
                if (mounted) setItems([]);
            });
        return () => {
            mounted = false;
        };
    }, [limit]);

    if (!items || items.length === 0) return null;

    return (
        <div
            className="bl-card p-5 md:p-6"
            data-testid="dashboard-shipped-feed"
        >
            <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-bl-primary" />
                <h3 className="font-display text-bl text-base leading-tight">
                    Shipped from feedback
                </h3>
            </div>
            <p className="mt-1 text-xs text-bl-muted leading-relaxed">
                Real changes shipped because you (or someone like you) asked.
            </p>
            <ul className="mt-3 space-y-3">
                {items.map((it) => (
                    <li
                        key={it.id}
                        className="text-sm leading-snug"
                        data-testid={`shipped-feed-item-${it.id}`}
                    >
                        <div className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-bl-primary mt-2 shrink-0" />
                            <div className="flex-1">
                                <div className="text-bl">{it.title}</div>
                                {it.from_request && (
                                    <div className="mt-0.5 text-[11px] text-bl-muted italic line-clamp-2">
                                        Asked: "{it.from_request}"
                                    </div>
                                )}
                                {it.link_url && (
                                    <a
                                        href={it.link_url}
                                        className="mt-1 inline-flex items-center gap-1 text-xs text-bl-primary hover:underline"
                                    >
                                        {it.link_label || "Read more"}
                                        <ArrowUpRight className="w-3 h-3" />
                                    </a>
                                )}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
